import { EventEmitter } from '@angular/core'
import {
  isCallResourceGroupApiResponse,
  isCallSentenceGroupApiResponse,
  type CallSentenceCitationApiResponse,
  type CitationsApiResponse,
} from '../../../../../backend/src/citations/minutes-citations.dto'
import { escapeRegExp } from '../../utils'

type EventEmitters = {
  openDocument: EventEmitter<{ resourceId: number; resourcePageNumber: number }>
  openMeeting: EventEmitter<void>
}

/**
 * For some reason TinyMCE only escapes certain characters as HTML entities so
 * we hardcode this conversion here in order to correctly match the content where
 * we need to insert citation tags.
 */
export function encodeTinyMceHtmlEntities(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/\£/g, '&pound;').replace(/€/g, '&euro;').replace(/±/g, '&plusmn;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

export function buildCallSentenceCitationItemHTMLTag(startSentenceId: string, endSentenceId: string, label: number, citationIds: string): string {
  return `<span class="citation-group-item citation-call-sentence mceNonEditable" data-start-sentence-id="${startSentenceId}" data-end-sentence-id="${endSentenceId}" data-citation-ids="${citationIds}">${label.toString()}</span>`
}

export function buildCallResourceCitationItemHTMLTag(resourceId: number, resourcePageNumber: number, label: number, citationIds: string): string {
  return `<span class="citation-group-item citation-call-resource mceNonEditable" data-call-resource-id="${resourceId.toString()}" data-call-resource-page-number="${resourcePageNumber.toString()}" data-citation-ids="${citationIds}">${label.toString()}</span>`
}

export function createCitationAddHighlightMouseEventHandler(editor: any, citationById: Record<string, CallSentenceCitationApiResponse>) {
  return function (event: MouseEvent) {
    const editorWindow: Window & typeof globalThis = editor.getWin()
    const target = event.target

    if (!(target instanceof editorWindow.HTMLElement)) {
      return
    }

    const dataCitationIds = target.getAttribute('data-citation-ids')

    if (dataCitationIds === null) {
      return
    }

    const highlightClasses = [
      'citation-highlight',
      target.classList.contains('citation-call-resource') ? 'citation-call-resource-highlight' : 'citation-call-sentence-highlight',
    ].join(' ')

    const sentencesToHighlight = Array.from(new Set(dataCitationIds.split(',').flatMap((citationId) => citationById[citationId]?.['content'] ?? [])))

    let editorContent = editor.getContent()
    let replaced = false

    for (const sentence of sentencesToHighlight) {
      const searchTerm = new RegExp(`(?<!(<span class="${highlightClasses}">))${escapeRegExp(encodeTinyMceHtmlEntities(sentence))}(?!(</span>))`, 'g')

      const replacer = (match: string) => {
        return `<span class="${highlightClasses}">${match}</span>`
      }

      if (!searchTerm.test(editorContent)) {
        continue
      }

      editorContent = editorContent.replace(searchTerm, replacer)
      replaced = true
    }

    if (replaced) {
      editor.setContent(editorContent)
    }
  }
}

export function createCitationRemoveHighlightMouseEventHandler(editor: any) {
  return function (event: MouseEvent) {
    const editorWindow: Window & typeof globalThis = editor.getWin()
    const target = event.target

    if (!(target instanceof editorWindow.HTMLElement)) {
      return
    }

    const highlightClasses = [
      'citation-highlight',
      target.classList.contains('citation-call-resource') ? 'citation-call-resource-highlight' : 'citation-call-sentence-highlight',
    ].join(' ')

    let editorContent = editor.getContent()

    const searchTerm = new RegExp(`<span class="${highlightClasses}">(.*?)</span>`, 'g')

    const replacer = (_: string, group: string) => group

    if (!searchTerm.test(editorContent)) {
      return
    }

    editorContent = editorContent.replace(searchTerm, replacer)
    editor.setContent(editorContent)
  }
}

export function createCitationClickMouseEventHandler(editor: any, eventEmitters: EventEmitters) {
  return function (event: MouseEvent) {
    const editorWindow: Window & typeof globalThis = editor.getWin()
    const target = event.target

    if (!(target instanceof editorWindow.HTMLElement)) {
      return
    }

    editor.selection.setCursorLocation(target.parentNode?.parentNode, 0)

    if (target.classList.contains('citation-call-sentence')) {
      eventEmitters.openMeeting.emit()
    } else if (target.classList.contains('citation-call-resource')) {
      const dataCallResourceId = target.getAttribute('data-call-resource-id')
      const dataCallResourcePageNumber = target.getAttribute('data-call-resource-page-number') ?? '1'

      if (dataCallResourceId === null) {
        console.error(`Call resource citation rendered element missing 'data-call-resource-id' attribute in click event handler.`)

        return
      }

      eventEmitters.openDocument.emit({ resourceId: parseInt(dataCallResourceId), resourcePageNumber: parseInt(dataCallResourcePageNumber) })
    } else {
      console.error(`Citation rendered element missing citation type class in click event handler.`)
    }
  }
}

export function registerCitationHighlightMouseEventHandlers({
  editor,
  citations,
  eventEmitters,
}: {
  editor: any
  citations: CitationsApiResponse
  eventEmitters: EventEmitters
}) {
  editor.on('SetContent', () => {
    const editorWindow: Window & typeof globalThis = editor.getWin()

    const citationById = citations
      .flatMap((citationGroup) => citationGroup.citations)
      .reduce((record: Record<string, CallSentenceCitationApiResponse>, citation: CallSentenceCitationApiResponse) => {
        record[citation.id.toString()] = citation

        return record
      }, {})
    const citationItemElements = Array.from(editorWindow.document.getElementsByClassName('citation-group-item'))

    for (const citationItemElement of citationItemElements) {
      if (!(citationItemElement instanceof editorWindow.HTMLElement)) {
        continue
      }

      const handleMouseEnter = createCitationAddHighlightMouseEventHandler(editor, citationById)
      const handleMouseLeave = createCitationRemoveHighlightMouseEventHandler(editor)
      const handleMouseClick = createCitationClickMouseEventHandler(editor, eventEmitters)

      citationItemElement.addEventListener('mouseenter', handleMouseEnter)
      citationItemElement.addEventListener('mouseleave', handleMouseLeave)
      citationItemElement.addEventListener('click', handleMouseClick)
    }
  })
}

export function insertCitationMetaTag(editor: any) {
  const metaTag = '<span id="citation-tags-inserted" style="display: none;">CITATIONS_RENDERED</span>'

  editor.getDoc()['body'].insertAdjacentHTML('afterbegin', metaTag)
}

export function isCitationMetaTagInserted(editor: any): boolean {
  return editor.getDoc()['body'].querySelector('span#citation-tags-inserted') !== null
}

export function insertCitationTags(editor: any, citations: CitationsApiResponse) {
  let editorContent = editor.getContent()

  const citationGroupsByMinuteNodeId = Object.groupBy(citations, (citation) => citation.minuteNodeId)

  for (const [, citationGroupWithSameMinuteNodeId] of Object.entries(citationGroupsByMinuteNodeId)) {
    if (typeof citationGroupWithSameMinuteNodeId === 'undefined') {
      continue
    }

    const citationGroupByContent = Object.groupBy(citationGroupWithSameMinuteNodeId, (citation) => citation.minuteNodeContent)

    let citationLabelIndex = 1

    for (const [, citationGroupWithSameContent] of Object.entries(citationGroupByContent)) {
      if (typeof citationGroupWithSameContent === 'undefined') {
        continue
      }

      let citationUpdatedContent = encodeTinyMceHtmlEntities(citationGroupWithSameContent[0].minuteNodeContent)
      let citationHtmlTags = ''

      for (let citationGroupIndex = 0; citationGroupIndex < citationGroupWithSameContent.length; citationGroupIndex++) {
        const citationGroup = citationGroupWithSameContent[citationGroupIndex]

        if (isCallSentenceGroupApiResponse(citationGroup)) {
          citationHtmlTags += buildCallSentenceCitationItemHTMLTag(
            citationGroup.startSentenceId,
            citationGroup.endSentenceId,
            citationLabelIndex,
            citationGroup.citations.map((citation) => citation.id.toString()).join(',')
          )

          citationLabelIndex++
        } else if (isCallResourceGroupApiResponse(citationGroup)) {
          // TODO: This is getting a bit messy, will refactor the whole function soon. - Geraint
          citationHtmlTags += citationGroup.citations
            .map((citation, index) =>
              buildCallResourceCitationItemHTMLTag(citation.resourceId, citation.resourcePageNumber, citationLabelIndex + index, citation.id.toString())
            )
            .join('')

          citationLabelIndex += citationGroup.citations.length
        }
      }

      const searchTerm = new RegExp(`<([a-z]+)([^>]*)>(.*?)${escapeRegExp(citationUpdatedContent)}(.*?)</\\1>`, 'g')

      const replacer = (_: string, group1: string, group2: string, group3: string, group4: string) => {
        return `<${group1}${group2}>${group3}${citationUpdatedContent}${group4} <span class="citation-group mceNonEditable">${citationHtmlTags}</span></${group1}>`
      }

      editorContent = editorContent.replace(searchTerm, replacer)
    }
  }

  editor.setContent(editorContent)
}

export function registerCitationTagsInsertionEventHandlers(editor: any, citations: CitationsApiResponse) {
  editor.on('SetContent', () => {
    if (isCitationMetaTagInserted(editor)) {
      return
    }

    insertCitationMetaTag(editor)
    insertCitationTags(editor, citations)
  })
}

export function initCitations({ editor, citations, eventEmitters }: { editor: any; citations: CitationsApiResponse; eventEmitters: EventEmitters }) {
  registerCitationHighlightMouseEventHandlers({ editor, citations, eventEmitters })
  registerCitationTagsInsertionEventHandlers(editor, citations)

  insertCitationMetaTag(editor)
  insertCitationTags(editor, citations)
}

/**
 * Strips citation annotations from a given content string.
 *
 * @param content - The content string from which citation annotations should be removed.
 *
 * @returns The content string with all citation annotations removed.
 */
export function stripCitationAnnotationsFromContent(content: string): string {
  return content.replace(/<span class=".*citation-highlight.*">.*<\/span>/g, '').replace(/<span class=".*citation-group.*">.*<\/span>/g, '')
}
